import React, { useState, useEffect } from 'react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textarea,
} from '@heroui/react';
import getToken from '@utility/getToken';
import TetraController from '@utility/TetraController';
import Logger from '@utility/services/Logger';
import styled from 'styled-components';

import { HR, Line, OR } from '@components/common/signInSignUp/newSignIn';
import { Black } from '@styling/theme';
import {
  InputLabelText,
  JobDescriptionWrap,
  Categories,
} from '../UI/pop-ups/jobDescriptionPopup/jdPopupComponents';
import data from '../UI/pop-ups/jobDescriptionPopup/data';
import {
  mapJdPopUp,
  mapReduxToJdPopUp,
} from './EditSpace/EditorPane/Pad/TetraPad/tetraEnums';
import tetraReviewModes from './EditSpace/EditorPane/Pad/TetraPad/utils/tetraReviewModes';

import scrollBar from '../UI/Editor/shared/scrollBar';
import QuestionGeneratorEnums from './EditSpace/EditorPane/Pad/questionGeneration/utils/enums';
import { AiJobDescription } from './aiJobDescription';

const StyledOR = styled(OR)`
  color: ${Black};
`;
export const CustomTextarea = styled(Textarea)`
  .input {
    ${() => scrollBar({ width: '6px', height: '6px' })}
  }
`;

const JobDescriptionPopup = ({
  jobDescriptions,
  jobDescriptionPopup,
  updateTetraDataDB,
  updateTetraData,
  yearOfExperience,
  setJobDescriptionPopup,
  evaluateATSScore,
  selectedTab,
  setSelectedTab,
  documentId,
  isQuestionsGenerator,
  handleQuestionsGenerator,
}) => {
  // This required for spinner
  // const [loading, setLoading] = useState({
  //   saveAndExit: false,
  //   evaluate: false,
  // });

  const { ACTIONS } = QuestionGeneratorEnums;

  const {
    ManualPasteSecion,
    ButtonsText,
    CategorySection,
    AIReviewPopup,
    JDReviewPopup,
  } = data;
  const { isOpen, isJdPopup, jdId } = jobDescriptionPopup;

  const {
    placeholderTexts: { JOB_TITLE, JOB_LOCATION, JOB_DESCRIPTION },
  } = ManualPasteSecion;

  const { options } = CategorySection;

  const { SAVE, CANCEL, CLEAR, SAVE_AND_EXIT, EVALUATE, GENERATE } =
    ButtonsText;

  const [expCategory, setExpCategory] = useState('');

  const [jobTitle, setJobTitle] = useState('');

  const [jobLocation, setJobLocation] = useState('');

  const [jobDescription, setJobDescription] = useState('');

  const clearInputData = () => {
    setExpCategory('');

    setJobTitle('');

    setJobLocation('');

    setJobDescription('');
  };

  useEffect(() => {
    setExpCategory(mapReduxToJdPopUp[yearOfExperience]);
    if (jdId) {
      const jobDescriptionData = jobDescriptions.filter(
        (jd) => jd.jdId === jdId,
      );
      const jdObj = jobDescriptionData[0];

      const { description, title, location } = jdObj;

      setJobTitle(title);
      setJobLocation(location);
      setJobDescription(description);
    } else {
      setJobTitle('');

      setJobLocation('');

      setJobDescription('');
    }
  }, [isOpen]);

  let updatedJobDescriptionData = {};

  if (isJdPopup) {
    updatedJobDescriptionData = {
      isJdUpdated: true,
      jd: {
        title: jobTitle,
        location: jobLocation,
        description: jobDescription,
      },
      jdId,
    };
  } else {
    updatedJobDescriptionData = {
      experience: mapJdPopUp[expCategory],
      jdId,
    };
  }

  const handleDoneClick = async () => {
    setJobDescriptionPopup({ isOpen: false });
    if (isQuestionsGenerator) {
      const dataToGenQuestions = {
        jdData: { ...updatedJobDescriptionData?.jd, jdId },
      };
      await handleQuestionsGenerator({
        data: dataToGenQuestions,
        action: !jdId ? ACTIONS.ADD_JD : ACTIONS.UPDATE_JD,
      });
    } else {
      if (selectedTab !== tetraReviewModes.SECTION_REVIEW) {
        setSelectedTab(tetraReviewModes.SECTION_REVIEW);
      }
      updateTetraData({ evaluatingResume: true });
      if (!isJdPopup) {
        await evaluateATSScore({
          data: updatedJobDescriptionData,
          isAllInputsRequire: true,
          jdId,
        });
      } else {
        await updateTetraDataDB(updatedJobDescriptionData);
      }
    }
  };

  const isSaveDisabled = () => !jobTitle || !jobLocation || !jobDescription;

  const isSaveDisabledAIReview = () => !expCategory;

  const isDisabled =
    jdId || isJdPopup ? isSaveDisabled() : isSaveDisabledAIReview();

  const saveAndExit = async () => {
    setJobDescriptionPopup({ isOpen: false });
    try {
      const token = await getToken();
      const dataObj = {
        documentId,
        ...updatedJobDescriptionData,
      };
      const jdRes = await TetraController.saveAndExit(token, dataObj);
      const { jdId: jobDesId } = jdRes;

      const filteredJobDescriptions = jobDescriptions.filter(
        (jobDes) => jobDes?.jdId !== jobDesId,
      );
      const updatedJobDescriptions = [
        {
          ...jdRes,
        },
        ...filteredJobDescriptions,
      ];
      updateTetraData({
        jobDescriptions: updatedJobDescriptions,
      });
    } catch (error) {
      Logger.LogError(error);
    }
  };
  const getButtonLabel = () => {
    if (isQuestionsGenerator) return GENERATE;
    if (!isJdPopup) return SAVE;
    return EVALUATE;
  };

  const setJobData = (JobData) => {
    setJobTitle(JobData.jobTitle);
    setJobLocation(JobData.location);
    setJobDescription(JobData.completeJobDescription);
  };

  return (
    <>
      <Modal
        backdrop="opaque"
        size="2xl"
        classNames={{
          wrapper: 'z-[103]',
          backdrop: 'z-[102]',
          closeButton: 'w-[40px] h-[40px]',
        }}
        isOpen={isOpen}
        onOpenChange={() => setJobDescriptionPopup({ isOpen: false })}
        motionProps={{
          variants: {
            enter: {
              y: 0,
              opacity: 1,
              transition: {
                duration: 0.3,
                ease: 'easeOut',
              },
            },
            exit: {
              y: -20,
              opacity: 0,
              transition: {
                duration: 0.2,
                ease: 'easeIn',
              },
            },
          },
        }}
      >
        <ModalContent
          className={`${isJdPopup ? 'w-[520px]' : 'w-[452px] h-[292px] desktopSm:w-[460px]  desktopSm:h-[312px] desktopMd:w-[600px]  desktopMd:h-[392px]'}`}
        >
          {() => (
            <>
              <ModalHeader
                className={`flex flex-col gap-1 px-[16px] py-[16px] pb-[8px] text-[#222222] font-normal text-[16px] ${!isJdPopup && 'desktopMd:px-[24px] desktopMd:py-[24px] desktopMd:pb-[12px]'}`}
              >
                {isJdPopup ? JDReviewPopup.heading : AIReviewPopup.heading}
              </ModalHeader>
              <ModalBody
                className={`px-[16px] ${!isJdPopup && 'desktopMd:px-[24px]'}`}
              >
                <JobDescriptionWrap>
                  {isJdPopup && (
                    <>
                      {' '}
                      <AiJobDescription
                        setJobData={setJobData}
                        jobTitle={jobTitle}
                      />
                      <div className="py-[8px] desktopSm:py-[8px] desktopMd:py-[8px]">
                        <HR>
                          <Line />
                          <StyledOR>OR</StyledOR>
                          <Line />
                        </HR>
                      </div>
                    </>
                  )}
                  <InputLabelText>
                    {isJdPopup
                      ? JDReviewPopup.subHeading
                      : AIReviewPopup.subHeading}
                  </InputLabelText>
                  {isJdPopup && (
                    <div className="grid gap-[12px]">
                      <Input
                        size="md"
                        variant="bordered"
                        isRequired
                        label={JOB_TITLE}
                        placeholder="Copy/paste job title"
                        defaultValue="junior@nextui.org"
                        value={jobTitle}
                        onChange={(e) => setJobTitle(e.target.value)}
                      />

                      <Input
                        size="md"
                        variant="bordered"
                        label={JOB_LOCATION}
                        isRequired
                        placeholder="Copy/paste job location"
                        value={jobLocation}
                        onChange={(e) => setJobLocation(e.target.value)}
                      />

                      <CustomTextarea
                        size="md"
                        variant="bordered"
                        label={JOB_DESCRIPTION}
                        isRequired
                        placeholder="Copy/paste job description"
                        value={jobDescription}
                        onChange={(e) => setJobDescription(e.target.value)}
                        className="h-[200px] "
                        classNames={{
                          innerWrapper: 'innerWrapper h-[200px]',
                          input: 'input',
                          inputWrapper: 'px-[16px] pr-[6px]',
                        }}
                      />
                    </div>
                  )}
                  {!isJdPopup && (
                    <Categories isJdPopup={isJdPopup}>
                      {options.map((option) => {
                        return (
                          <Button
                            key={`${option}`}
                            className={`h-[48px] w-[132px] desktopSm:w-[132px]  desktopSm:h-[56px] desktopMd:w-[168px]  desktopMd:h-[80px] text-[12px] desktopMd:text-[14px] drop-shadow ${option === expCategory ? 'bg-[#0070F0] text-[#ffffff] font-medium' : 'bg-[#FFFFFF] text-[#444746]'}`}
                            onClick={() => {
                              setExpCategory(option);
                            }}
                          >
                            {option}
                          </Button>
                        );
                      })}
                    </Categories>
                  )}
                </JobDescriptionWrap>
              </ModalBody>
              <ModalFooter
                className={`p-[16px] gap-[16px] ${isJdPopup ? 'pt-[8px]' : 'desktopMd:p-[24px]'}`}
              >
                {!isJdPopup && (
                  <Button
                    size="sm"
                    onClick={() => {
                      setJobDescriptionPopup({
                        isOpen: false,
                      });
                    }}
                    radius="full"
                    className="text-[#444746]"
                  >
                    {CANCEL}
                  </Button>
                )}

                {isJdPopup && (
                  <Button
                    radius="full"
                    size="sm"
                    className="text-[#444746]"
                    onClick={clearInputData}
                  >
                    {CLEAR}
                  </Button>
                )}

                {isJdPopup && (
                  <Button
                    size="sm"
                    color="primary"
                    radius="full"
                    onClick={!isDisabled ? saveAndExit : {}}
                    isDisabled={isDisabled}
                  >
                    {SAVE_AND_EXIT}
                    {/* {SAVE_AND_EXIT} <Spinner size="sm" color="default" /> */}
                  </Button>
                )}
                <Button
                  size="sm"
                  color="primary"
                  radius="full"
                  onClick={!isDisabled ? handleDoneClick : {}}
                  isDisabled={isDisabled}
                >
                  {getButtonLabel()}
                  {/* <Spinner size="sm" color="default" /> */}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default JobDescriptionPopup;
